<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{params.type === 'edit' ? $t('table.action.modify') : $t('report.create_report_element')}}</div>
    <div slot="body">
      <a-form :form="form.fc" v-bind="formLayout">
        <!-- 域 -->
        <a-form-item :label="$t('dictionary.domain')">
          <domain-select v-if="isAdminMode && l3PermissionEnable" v-decorator="decorators.project_domain_id" :params="domainParams" :disabled="isEditType" @change="handleDomainChange" />
          <template v-else> {{userInfo.projectDomain}} </template>
        </a-form-item>
        <!-- 类型 -->
        <a-form-item :label="$t('report.element_type')">
          <a-radio-group v-decorator="decorators.element_type" :disabled="isEditType" @change="handleElementTypeChange">
            <a-radio-button v-for="item of ELEMENT_TYPES" :value="item.key" :key="item.key">
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <!-- 指标 -->
        <a-form-item v-if="['usage', 'metadata'].includes(form.fd.element_type)" :key="'ek-' + form.fd.element_type" :label="form.fd.element_type === 'metadata' ? $t('report.tag_key') : $t('report.element_key')">
          <base-select
            v-decorator="decorators.element_key"
            :options="elementKeys"
            :disabled="isEditType"
            :select-props="{ placeholder: $t('common.tips.select', [form.fd.element_type === 'metadata' ? $t('report.tag_key') : $t('report.element_key')])}" />
        </a-form-item>
        <a-form-item v-if="['metric'].includes(form.fd.element_type)" :key="'ek-metric'" :label="$t('report.element_key')">
          <select-or-input v-decorator="decorators.element_key" :disabled="isEditType" :name="$t('report.element_key')" valueType="Object" :options="elementKeys" forceInitType="select" @change="elementKeyChange" />
        </a-form-item>
        <a-form-item v-else-if="['constant'].includes(form.fd.element_type)" :label="$t('report.element_value')">
          <a-input v-decorator="decorators.element_value" :disabled="isEditType" :placeholder="$t('common.tips.input', [$t('report.element_value')])" />
        </a-form-item>
        <!-- 属性 -->
        <a-form-item v-else-if="['attribute'].includes(form.fd.element_type)" :label="$t('report.attribute_key')">
          <select-or-input v-decorator="decorators.attribute" :disabled="isEditType" :name="$t('report.attribute_key')" valueType="Object" :options="attributeOpts" forceInitType="select" @change="attributeChange" />
        </a-form-item>
        <!-- 名称 -->
        <a-form-item :label="$t('report.element_name')" :extra="$t('report.report_element_explain')">
          <div class="d-flex align-items-center">
            <a-input v-decorator="decorators.name" :placeholder="$t('common.tips.input', [$t('report.element_name')])" />
            <div class="ml-2 d-flex align-items-center" v-if="['binary', 'string', 'bps', 'percent'].includes(unitOpt.type)">
              <span style="width: 50px">{{$t('report.unit')}}:</span>
              <a-select v-if="['binary', 'bps'].includes(unitOpt.type)" v-model="form.fd.unit" style="width: 80px">
                <a-select-option v-for="item in unitOpt.value" :value="item" :key="item">{{item}}</a-select-option>
              </a-select>
              <template v-else>{{unitOpt.value}}</template>
            </div>
          </div>
        </a-form-item>
        <!-- 字段类型 -->
        <a-form-item :label="$t('report.value_type')">
          <base-select
            v-decorator="decorators.value_type"
            :options="valueTypeOpts"
            :disabled="isEditType"
            :select-props="{ placeholder: $t('common.tips.select', [$t('report.value_type')])}" />
        </a-form-item>
        <!-- 值表达式 -->
        <a-form-item v-if="['int', 'float'].includes(form.fd.value_type)" :label="$t('report.value_expression')" :extra="$t('report.value_expression_tip')">
          <a-input v-decorator="decorators.calc" :placeholder="$t('common.tips.input', [$t('report.value_expression')])" />
        </a-form-item>
        <!-- 时间范围 -->
        <a-form-item v-if="['metric'].includes(form.fd.element_type)" :label="$t('report.time_range')">
          <div class="d-flex align-items-center">
            <a-input-number v-decorator="decorators.period" :disabled="isEditType" :min="0.1" :placeholder="$t('common.tips.input', [$t('report.time_range')])" style="width: 100%" />
            <base-select class="ml-2" v-decorator="decorators.period_unit" :disabled="isEditType" :options="periodUnitOpts" :select-props="{ placeholder: $t('common.tips.select', [$t('report.time_range_unit')])}" />
          </div>
        </a-form-item>
      </a-form>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import * as R from 'ramda'
import { mapGetters } from 'vuex'
import { ELEMENT_TYPES, REPORT_USAGES, REPORT_METRICS, PERIOD_UNIT_OPTS, ATTRIBUTE_KEY_OPTS } from '@Reports/constants'
import { reportUnitTransfer } from '@Reports/utils'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import DomainSelect from '@/sections/DomainSelect'
// import { getCurrencyOptions } from '@Bill/utils'

export default {
  name: 'ReportElementCreateDialog',
  components: {
    DomainSelect,
  },
  mixins: [DialogMixin, WindowsMixin],
  data () {
    const data = this.params.type === 'edit' ? this.params.data[0] : {}
    const {
      name = '',
      domain_id = (this.params.domain && this.params.domain.key) || this.$store.getters.userInfo.projectDomainId,
      element_type = 'usage',
      element_key = '',
      value_type = '',
      calc = 'x',
      period = 5,
      period_unit = 'hour',
      element_value = '',
    } = data
    const initElementKey = ['usage', 'metadata'].includes(element_type) ? element_key : { id: element_key }
    const initElementAttribute = ['attribute'].includes(element_type) ? { id: element_key } : {}
    return {
      ELEMENT_TYPES,
      REPORT_USAGES,
      REPORT_METRICS,
      tagOpts: [],
      isEditType: this.params.type === 'edit',
      form: {
        fc: this.$form.createForm(this, {
          onValuesChange: (props, values) => {
            Object.keys(values).forEach((key) => {
              const val = values[key]
              // select-or-input 会原地改对象，这里拷贝一份以触发依赖 element_key 的计算属性
              this.form.fd[key] = (key === 'element_key' && val && typeof val === 'object') ? { ...val } : val
            })
            if (values.hasOwnProperty('element_key')) {
              this.fillNameUseElementKey(this.form.fd.element_key)
            }
          },
        }),
        fd: {
          element_type,
          element_key,
          value_type,
          unit: '',
          attribute: '',
        },
      },
      decorators: {
        name: [
          'name',
          {
            initialValue: name,
            validateFirst: true,
            rules: [
              { required: true, message: this.$t('common.tips.input', [this.$t('report.element_name')]) },
              // { validator: this.$validate('resourceName') },
            ],
          },
        ],
        project_domain_id: [
          'project_domain_id',
          {
            initialValue: domain_id,
            rules: [
              { required: true, message: this.$t('cloudenv.text_284', [this.$t('dictionary.domain')]) },
            ],
          },
        ],
        element_type: [
          'element_type',
          {
            initialValue: element_type,
            rules: [
              { required: true },
            ],
          },
        ],
        element_key: [
          'element_key',
          {
            initialValue: initElementKey,
            rules: [
              { required: true, message: this.$t('common.tips.select', [this.$t('report.element_key')]) },
            ],
          },
        ],
        element_value: [
          'element_value',
          {
            initialValue: element_value,
            rules: [
              { required: true, message: this.$t('common.tips.input', [this.$t('report.element_value')]) },
            ],
          },
        ],
        unit: ['unit', {
          rules: [
            { required: true },
          ],
        }],
        attribute: ['attribute', {
          initialValue: initElementAttribute,
          rules: [
            { required: true, message: this.$t('common.tips.input', [this.$t('report.attribute_key')]) },
          ],
        }],
        value_type: ['value_type', {
          initialValue: value_type,
          rules: [
            { required: true, message: this.$t('common.tips.select', [this.$t('report.value_type')]) },
          ],
        }],
        calc: ['calc', {
          initialValue: calc,
          rules: [{ required: true, message: this.$t('common.tips.input', [this.$t('report.value_expression')]) }],
        }],
        period: ['period', {
          initialValue: period,
          rules: [{ required: true, message: this.$t('common.tips.input', [this.$t('report.time_range')]) }],
        }],
        period_unit: ['period_unit', {
          initialValue: period_unit,
        }],
      },
      formLayout: {
        wrapperCol: {
          span: 20,
        },
        labelCol: {
          span: 4,
        },
      },
      domainParams: {
        limit: 0,
        filter: 'enabled.equals(1)',
      },
      projectDomainId: domain_id,
      attributeOpts: ATTRIBUTE_KEY_OPTS.map(item => ({ ...item, name: item.id + `（${item.name}）` })),
      valueTypeOpts: [
        { id: 'int', name: this.$t('report.value_type.int') },
        { id: 'float', name: this.$t('report.value_type.float') },
        { id: 'string', name: this.$t('report.value_type.string') },
      ],
      periodUnitOpts: PERIOD_UNIT_OPTS,
    }
  },
  computed: {
    ...mapGetters(['isAdminMode', 'isDomainMode', 'userInfo', 'l3PermissionEnable']),
    // ...mapState('common', {
    //   currency: state => state.bill.currency,
    //   currencyOpts: state => state.bill.currencyOpts,
    // }),
    elementKeys () {
      const { element_type } = this.form.fd
      if (element_type === 'usage') {
        return REPORT_USAGES.map(item => {
          return { ...item, key: item.key, origin_label: item.label, label: `${item.label}（${item.key}）` }
        })
      }
      if (element_type === 'metric') {
        return REPORT_METRICS.map(item => {
          return { ...item, id: item.key, origin_label: item.label, name: `${item.label}（${item.key}）` }
        })
      }
      if (element_type === 'metadata') {
        return this.tagOpts
      }
      return []
    },
    unitOpt () {
      const ret = {
        type: '',
        origin_unit: '',
      }
      const { element_type, element_key } = this.form.fd
      const elementKey = (element_key && typeof element_key === 'object') ? (element_key.id || element_key.name) : element_key
      let targetElements = REPORT_USAGES
      if (element_type === 'metric') {
        targetElements = REPORT_METRICS
      }
      const targetType = targetElements.filter(item => item.key === elementKey)
      if (targetType[0]) {
        ret.type = targetType[0].unit_type
        if (ret.type === 'string' || ret.type === 'percent') {
          ret.value = targetType[0].origin_unit
        } else if (ret.type === 'binary') {
          ret.value = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
          ret.origin_unit = targetType[0].origin_unit
        } else if (ret.type === 'bps') {
          ret.value = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps', 'Pbps']
        }
      }
      return ret
    },
    // currencyOptions () {
    //   return getCurrencyOptions()
    // },
  },
  watch: {
    unitOpt: {
      handler: function (val) {
        if (val.type === 'binary' || val.type === 'bps') {
          this.form.fc.setFieldsValue({
            unit: val.value.includes(val.origin_unit) ? val.origin_unit : val.value[0],
          })
        } else {
          this.form.fc.setFieldsValue({
            unit: '',
          })
        }
      },
    },
  },
  created () {
    this.fetchTags()
  },
  methods: {
    async fetchTags () {
      let manager = new this.$Manager('metadatas')
      try {
        const response = await manager.get({
          id: 'tag-value-pairs',
          params: {
            limit: 0,
            scope: this.$store.getters.scope,
            with_user_meta: true,
            with_cloud_meta: true,
            resources: 'project',
          },
        })
        const data = response.data.data || []
        // 将已显示的标签列进行合并
        let tags = data.map(item => item.key)
        tags = R.uniq(tags)
        this.tagOpts = tags.map(key => {
          return { key, label: key.replace('user:', '') }
        })
      } catch (error) {
        throw error
      } finally {
        manager = null
      }
    },
    fillNameUseElementKey (key) {
      const realKey = (key && typeof key === 'object') ? (key.id || key.name) : key
      if (!realKey) return
      const target = this.elementKeys.filter(item => item.key === realKey || item.id === realKey)
      const name = target[0] ? target[0].origin_label || target[0].label : ''
      if (name) {
        this.form.fc.setFieldsValue({
          name,
        })
      }
    },
    doCreate (data) {
      return new this.$Manager('project_mappings').create({ data: data })
    },
    handleElementTypeChange (val) {
      const type = val.target.value
      // metric 的 select-or-input 需要 Object；若仍是 string，组件原地赋值不会生效
      const elementKey = type === 'metric' ? { id: '', name: '' } : undefined
      this.form.fd.element_key = elementKey
      this.form.fc.setFieldsValue({
        element_key: elementKey,
        element_value: undefined,
        ...(type === 'metric' ? { value_type: 'float' } : {}),
        ...(type === 'meter' ? { name: this.$t('scope.text_254') } : {}),
      })
    },
    genCreateParams (values) {
      const params = { ...values }
      const { element_type, element_key } = params
      if (['usage'].includes(element_type)) {
        const target = REPORT_USAGES.filter(item => item.key === element_key)
        params.service_type = target[0].service
        // 单位转换
        if (target[0].unit_type === 'binary') {
          const unitCalc = reportUnitTransfer.getBinaryCalc(target[0].origin_unit, this.form.fd.unit)
          if (unitCalc !== 'x') {
            // params.calc = unitCalc
          }
          if (reportUnitTransfer.isBinaryUnit(this.form.fd.unit)) {
            params.name += `（${this.form.fd.unit}）`
          }
        } else if (target[0].unit_type === 'string') {
          params.name += `（${target[0].origin_unit}）`
        } else if (target[0].unit_type === 'percent') {
          params.name += `（${target[0].origin_unit}）`
          // params.calc = 'x * 10'
        }
      } else if (['metric'].includes(element_type)) {
        let elementKey = element_key
        if (elementKey?.name && !elementKey?.id) {
          elementKey = elementKey.name
          params.element_key = elementKey
        } else {
          elementKey = elementKey.id
          params.element_key = elementKey
        }
        const target = REPORT_METRICS.filter(item => item.key === elementKey)
        // params.period = target[0].period
        // 单位转换
        if (target[0]) {
          if (target[0].unit_type === 'bps') {
            const unitCalc = reportUnitTransfer.getBpsCalc(target[0].origin_unit, this.form.fd.unit)
            if (unitCalc !== 'x') {
              // params.calc = unitCalc
            }
            if (reportUnitTransfer.isBpsUnit(this.form.fd.unit)) {
              params.name += `（${this.form.fd.unit}）`
            }
          } else if (target[0].unit_type === 'string') {
            params.name += `（${target[0].origin_unit}）`
          } else if (target[0].unit_type === 'percent') {
            params.name += `（${target[0].origin_unit}）`
            // params.calc = 'x * 10'
          }
        }
      } else if (['meter'].includes(element_type)) {
        // const meterParams = decodeCurrencyParamsFromCurrency(element_key)
        // if (!meterParams.disable_cost_conversion) {
        //   meterParams.secondary_price = true
        // }
        // // TODO filter参数是否生效
        // params.element_key = element_key.replace('_', '').replace('*', '')
        // params = { ...params, ...meterParams }
        params.element_key = 'CNY'
      }
      if (params.attribute?.id) {
        params.element_key = params.attribute.id
        delete params.attribute
      } else if (params.attribute?.name) {
        params.element_key = params.attribute.name
        delete params.attribute
      }
      return params
    },
    attributeChange (val) {
      if (val.id && val.name) {
        const target = this.attributeOpts.filter(item => item.id === val.id)
        if (target[0]) {
          this.form.fc.setFieldsValue({
            value_type: target[0].value_type || 'string',
          })
        }
      }
    },
    elementKeyChange (val) {
      if (val.id && val.name) {
        const target = this.elementKeys.filter(item => item.id === val.id)
        if (target[0]) {
          this.form.fc.setFieldsValue({
            value_type: target[0].value_type || 'string',
          })
          setTimeout(() => {
            this.form.fc.setFieldsValue({
              calc: target[0].calc || 'x',
            })
          }, 100)
        }
      }
      // if (val.)
    },
    async handleConfirm () {
      this.loading = true
      try {
        const values = await this.form.fc.validateFields()
        if (this.isEditType) {
          await this.params.onManager('update', {
            id: this.params.data[0].id,
            managerArgs: {
              data: {
                name: values.name,
                calc: values.calc,
              },
            },
          })
        } else {
          const params = this.genCreateParams(values)
          await this.params.onManager('create', {
            managerArgs: {
              data: params,
            },
          })
        }
        this.cancelDialog()
      } catch (error) {
        throw error
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
